import React from 'react';
import { Link } from 'react-router-dom';

/**
 * BrandLogo Component - Single source of truth for Stay Pair Homes branding
 * 
 * Usage:
 * - <BrandLogo /> - Default size
 * - <BrandLogo size="sm" /> - Small size for compact spaces
 * - <BrandLogo size="lg" /> - Large size for hero sections
 * - <BrandLogo inverted /> - White/inverted version for dark backgrounds
 * - <BrandLogo linkTo="/" /> - Makes logo clickable (default)
 * - <BrandLogo linkTo={null} /> - No link wrapper
 */

const LOGO_PATH = '/staypairhomes-logo.png';

const SIZE_CLASSES = {
  xs: 'h-8 sm:h-10',
  sm: 'h-12 sm:h-14',
  md: 'h-16 sm:h-20 md:h-24',
  lg: 'h-20 sm:h-24 md:h-28',
  xl: 'h-24 sm:h-28 md:h-32'
};

export const BrandLogo = ({ 
  size = 'md', 
  inverted = false, 
  linkTo = '/', 
  className = '',
  showText = false,
  textClassName = ''
}) => {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const invertedClass = inverted ? 'brightness-0 invert' : '';
  
  const logoElement = (
    <div className={`flex items-center ${className}`} data-testid="brand-logo">
      <img 
        src={LOGO_PATH} 
        alt="Stay Pair Homes" 
        className={`${sizeClass} w-auto object-contain ${invertedClass}`}
      />
      {showText && (
        <span className={`font-heading font-bold text-slate-900 ${textClassName}`}>
          Stay Pair Homes
        </span>
      )}
    </div>
  );
  
  if (linkTo) {
    return (
      <Link to={linkTo} className="inline-flex items-center" data-testid="brand-logo-link">
        {logoElement}
      </Link>
    );
  }
  
  return logoElement;
};

// Compact variant for auth pages and forms - uses actual logo, not icon placeholder
export const BrandLogoCompact = ({ linkTo = '/', className = '' }) => {
  const logoElement = (
    <div className={`flex items-center gap-2 ${className}`} data-testid="brand-logo-compact">
      <img 
        src={LOGO_PATH} 
        alt="Stay Pair Homes" 
        className="h-16 sm:h-20 w-auto object-contain"
      />
    </div>
  );
  
  if (linkTo) {
    return (
      <Link to={linkTo} className="inline-flex items-center" data-testid="brand-logo-compact-link">
        {logoElement}
      </Link>
    );
  }
  
  return logoElement;
};

export default BrandLogo;
